<template>
	<div v-for="(category, index) in sortedList" :key="index">
		<strong v-if="category.name" class="block mt-3 mb-2 text-sm text-body-primary">
			{{ category.name }}
			<template v-if="category.items">
				({{ category.items.length }})
			</template>
		</strong>
		<ul v-if="category.items" class="mb-2" role="list">
			<li
				v-for="item in category.items"
				:key="item.ID"
				class="py-2 sm:py-3 border-b border-border-secondary"
				:data-item-id="item.ID"
			>
				<div
					v-if="isTextShoppingListItem(item) && isEditingItem(item.ID)"
					class="flex flex-col gap-2 mb-2"
				>
					<FormKit
						v-model="editText"
						type="text"
						label="Upravit text položky"
						:maxlength="100"
						:auto-focus="true"
						:classes="editInputClasses"
						:disabled="isUpdatePending(item.ID)"
						@keydown.enter.prevent="saveEdit(item)"
						@keydown.esc.prevent="cancelEdit"
					/>
					<div class="flex gap-2 justify-end">
						<button
							type="button"
							class="btn btn-secondary btn-xs"
							:disabled="isUpdatePending(item.ID)"
							@click="saveEdit(item)"
						>
							Uložit
						</button>
						<button
							type="button"
							class="btn btn-tertiary btn-xs"
							:disabled="isUpdatePending(item.ID)"
							@click="cancelEdit"
						>
							Zrušit
						</button>
					</div>
				</div>
				<div v-else class="flex gap-4 items-center mb-2">
					<div
						v-if="isTextShoppingListItem(item)"
						class="flex gap-3 items-center min-w-0"
					>
						<span class="flex items-center justify-center w-[2.75rem] h-[2.75rem] shrink-0 text-icon-primary">
							<NuxtIcon name="custom/handwrite" filled class="text-2xl" />
						</span>
						<div class="leading-[1.1] min-w-0">
							<h4 class="text-heading-primary typo-body-caption mb-0">
								{{ item.Text }}
							</h4>
						</div>
					</div>
					<AbsoluteInternalLink
						v-else
						:to="branchSpecificPath({
							name: 'hypermarket-cela-nabidka-category-p-product',
							params: {product: formatProductSlug(item.Text, item.VANR)},
						})"
						class="flex gap-3 items-center min-w-0"
						@click="$emit('closeDropdown')"
					>
						<NuxtImg
							v-if="item.productDetail?.imgDetail"
							:src="item.productDetail?.imgDetail"
							:alt="item.Text || ''"
							class="block w-[2.75rem] max-h-[2.75rem] max-w-none object-contain"
							loading="lazy"
						/>
						<NuxtImg
							v-else
							src="/images/product-placeholder.jpg"
							:alt="item.Text"
							class="block w-[2.75rem] max-w-none"
							width="44"
							loading="lazy"
						/>

						<div class="leading-[1.1] min-w-0">
							<h4 class="text-heading-primary typo-body-caption mb-0">
								{{ item.Text }}
							</h4>
							<span class="typo-body-caption text-body-tertiary lowercase">
								{{ item.productDetail?.unitAmount }} {{ item.productDetail?.unitId }}
							</span>
						</div>
					</AbsoluteInternalLink>
					<div class="ml-auto flex gap-2 shrink-0">
						<button
							v-if="isTextShoppingListItem(item)"
							type="button"
							class="btn btn-secondary btn-icon text-icon-primary"
							aria-label="Upravit text položky"
							@click="startEdit(item)"
						>
							<NuxtIcon name="edit-2" class="text-base" filled />
						</button>
						<button
							type="button"
							class="btn btn-secondary btn-icon text-icon-primary"
							:disabled="isDeletePending(item.ID)"
							@click="$emit('deleteItem', item)"
						>
							<Loading :is-loading="isDeletePending(item.ID)">
								<NuxtIcon
									name="trash-2"
									class="text-base"
									filled
								/>
							</Loading>
						</button>
					</div>
				</div>

				<div v-if="!isTextShoppingListItem(item)" class="flex items-center gap-3">
					<PlusMinus
						#default="{value}"
						:model-value="item.Amount ?? 0"
						:max="99"
						icon-button-style
						:disabled="isUpdatePending(item.ID)"
						@update:model-value="(amount: any) => $emit('updateAmount', item, amount)"
					>
						<span class="text-center min-w-[2ch] typo-body-m">{{ value }}</span>
					</PlusMinus>
					<strong v-if="item.productDetail?.productInHouse" class="text-neutral-700 ml-auto">
						<ProductTriplePricing
							:product-in-house="item.productDetail.productInHouse"
							:amount="item.Amount"
							small-price
						/>
					</strong>
				</div>
			</li>
		</ul>
	</div>
</template>

<script setup lang="ts">
	import {isTextShoppingListItem, sortedShoppingListByCategory, type GsoaShoppingListGetShoppingListresponseWithProductDetail} from '~/utils/shoppingList';
	import Loading from '~/components/ui/Loading.vue';
	import type {PendingStates} from '~/components/shoppingList/MenuDialog.vue';
	import type {Gsoa_ShoppingList_GetItemsCollection as GsoaShoppingListGetItemsCollection} from '~/openapi';
	import {formatProductSlug} from '~/utils/createHouseProductSlug';

	interface Props {
		items: GsoaShoppingListGetShoppingListresponseWithProductDetail[]
		pendingStates: PendingStates
	}

	interface Emits {
		(e: 'deleteItem', item: GsoaShoppingListGetItemsCollection): void
		(e: 'updateAmount', item: GsoaShoppingListGetItemsCollection, amount: number): void
		(e: 'updateText', item: GsoaShoppingListGetItemsCollection, text: string): void
		(e: 'closeDropdown'): void
	}

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const branchSpecificPath = useBranchSpecificPath();

	const editingItemId = ref<number | null>(null);
	const editText = ref('');

	const editInputClasses = {
		outer: 'w-full',
		wrapper: 'w-full',
		inner: 'max-w-none',
		label: 'sr-only',
		input: 'typo-body-m text-heading-primary py-2',
	};

	const isDeletePending = (id: number) => unref(props.pendingStates.delete[id]) ?? false;

	const isUpdatePending = (id: number) => unref(props.pendingStates.update[id]) ?? false;

	const sortedList = computed(() => sortedShoppingListByCategory(props.items));

	function isEditingItem(id: number) {
		return editingItemId.value === id;
	}

	function startEdit(item: GsoaShoppingListGetItemsCollection) {
		editingItemId.value = item.ID;
		editText.value = item.Text ?? '';
	}

	function cancelEdit() {
		editingItemId.value = null;
		editText.value = '';
	}

	function saveEdit(item: GsoaShoppingListGetItemsCollection) {
		const trimmed = editText.value.trim();
		if (!trimmed) {
			return;
		}
		if (trimmed === item.Text) {
			cancelEdit();
			return;
		}
		emit('updateText', item, trimmed);
		cancelEdit();
	}
</script>
